import React, { useState, useContext, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { EditModeContext } from "../../context/editModeContext/editMode.context";
import { AuthenticationContext } from "../../context/authentication/authentication.context";
import { useSidebar } from "../../context/sidebar/sidebar.context";

import SideMenuLink from "../side-menu-link/side-menu-link.component";

import Logo from "../../assets/image_1-removebg-preview 1.svg";
import managementIcon from "../../assets/Group.svg";
import accountIcon from "../../assets/friends.svg";
import chart from "../../assets/chart.svg";
// import courseIcon from "../../assets/open-book.svg";
import logOutIcon from "../../assets/logOut.svg";
import annIcon from "../../assets/ann.svg";
import salesCoachIcon from "../../assets/sales-coach-icon.svg";

const SideMenu = () => {
    const history = useHistory();
    const location = useLocation();
    const sideMenuRef = useRef(null);

    const [currentLink, setCurrentLink] = useState(window.location.pathname);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const { editMode, changeEditMode } = useContext(EditModeContext);
    const { signOutUser, currentUser } = useContext(AuthenticationContext);
    const { isCollapsed, toggleSidebar } = useSidebar();

    const salesCoachEnabled = !!(currentUser && currentUser["operator/salesCoachEnabled?"]);

    // Sync currentLink with location changes
    useEffect(() => {
        setCurrentLink(location.pathname);
    }, [location.pathname]);

    const handleClick = (pathTo) => {
        setCurrentLink(pathTo);
        setIsMobileMenuOpen(false); // Close mobile menu when link is clicked

        if (editMode === true) changeEditMode();
    };

    const signOut = async () => {
        try {
            const result = await signOutUser();

            if (result.success) {
                // Redirect to login page on successful logout
                history.push("/");
            } else {
                // Even if signout had issues, still redirect
                console.error("Logout had issues:", result.error);
                history.push("/");
            }
        } catch (error) {
            console.error("Error during logout:", error);
            // Redirect anyway to ensure user gets to login page
            history.push("/");
        }
    };

    // Close mobile menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sideMenuRef.current && !sideMenuRef.current.contains(event.target)) {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Hamburger menu component for mobile
    const HamburgerButton = () => (
        <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden fixed top-6 left-6 z-50 p-2 rounded-md bg-white shadow-lg border border-gray-200"
            aria-label="Toggle menu"
        >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className={`bg-plumb block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${isMobileMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'}`}></span>
                <span className={`bg-plumb block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`bg-plumb block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${isMobileMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-0.5'}`}></span>
            </div>
        </button>
    );

    // Collapse button for desktop
    const CollapseButton = () => (
        <button
            onClick={toggleSidebar}
            className="hidden lg:flex absolute top-6 right-4 z-50 p-2 rounded-md bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
            aria-label="Toggle sidebar"
        >
            <svg
                className="w-5 h-5 text-plumb transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={isCollapsed ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"}
                />
            </svg>
        </button>
    );

    return (
        <>
            <HamburgerButton />

            {/* Backdrop for mobile */}
            {isMobileMenuOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            <div
                ref={sideMenuRef}
                className={`
          h-full box-border bg-white border-r border-grey300 z-40
          lg:relative lg:translate-x-0
          fixed top-0 left-0
          transform transition-all duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isMobileMenuOpen ? 'w-full max-w-full' : (isCollapsed ? 'lg:w-20 lg:max-w-20' : 'lg:w-80 lg:max-w-80 w-80 max-w-80')}
        `}
            >
                <CollapseButton />
                <div className={`flex h-full flex-col items-center ${(isCollapsed && !isMobileMenuOpen) ? 'p-4' : 'p-10'} pb-12`}>
                    {(!isCollapsed || isMobileMenuOpen) && (
                        <img
                            src={Logo}
                            alt="logo"
                            height="40"
                            width="137"
                            className="transition-all duration-300"
                        />
                    )}
                    <div className={`flex flex-col text-center ${(isCollapsed && !isMobileMenuOpen) ? 'items-center mt-0' : 'mt-10'} w-full h-full`}>
                        {/* Add a spacer to push sign out to bottom when collapsed (but not on mobile) */}
                        {(isCollapsed && !isMobileMenuOpen) && <div className="flex-1"></div>}
                        <SideMenuLink
                            text="Dashboard"
                            icon={chart}
                            pathTo="/dashboard"
                            currentLink={currentLink}
                            handleClick={() => handleClick("/dashboard")}
                            isMobileMenuOpen={isMobileMenuOpen}
                        />

                        <SideMenuLink
                            text="Announcements"
                            icon={annIcon}
                            pathTo="/announcements/all"
                            currentLink={currentLink}
                            handleClick={() => {
                                // If we're already on an announcements page, stay on the current view
                                // Otherwise go to /announcements/all
                                if (currentLink.includes('/announcements/')) {
                                    handleClick(currentLink);
                                } else {
                                    handleClick("/announcements/all");
                                }
                            }}
                            isMobileMenuOpen={isMobileMenuOpen}
                        />

                        {salesCoachEnabled && (
                            <SideMenuLink
                                text="Sales Coach"
                                icon={salesCoachIcon}
                                pathTo="/sales-coach"
                                currentLink={currentLink}
                                handleClick={() => handleClick("/sales-coach")}
                                isMobileMenuOpen={isMobileMenuOpen}
                            />
                        )}

                        <SideMenuLink
                            text="Management"
                            icon={managementIcon}
                            pathTo="/management/staff"
                            currentLink={currentLink}
                            handleClick={() => handleClick("/management")}
                            subLinks={[
                                {
                                    text: "Staff",
                                    pathTo: "/management/staff",
                                },
                                {
                                    text: "Teams",
                                    pathTo: "/management/teams",
                                },
                            ]}
                            isMobileMenuOpen={isMobileMenuOpen}
                        />

                        <SideMenuLink
                            text="Account"
                            icon={accountIcon}
                            pathTo="/account/info"
                            currentLink={currentLink}
                            handleClick={() => handleClick("/account/info")}
                            isMobileMenuOpen={isMobileMenuOpen}
                        />

                        <div
                            onClick={signOut}
                            className={`border-t-2 border-creole border-opacity-10 flex flex-row items-end justify-center pt-12 cursor-pointer flex-1 mt-24 ${isCollapsed ? 'justify-center' : ''}`}
                            title={isCollapsed ? "Sign out" : ""}
                        >
                            <img src={logOutIcon} alt="" className={isCollapsed ? 'mx-auto' : ''} />
                            {!isCollapsed && (
                                <p className="ml-7 text-base font-extrabold text-sunsetOrange">
                                    Sign out
                                </p>
                            )}
                        </div>
                        {/* <Link to="/course/overview">
              <SideMenuLink
                text="Course"
                icon={courseIcon}
                pathTo="/course/overview"
                currentLink={currentLink}
                handleClick={() => handleClick("/course/overview")}
              />
            </Link> */}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SideMenu;